<template>
  <div class="book-slider">
    <div class="book-slider__container">
      <h4 class="book-slider__headline">
        {{ headline }}
      </h4>

      <div class="book-slider__books-counter">
        {{ $t("book") }} {{ currentBookNumber }} {{ $t("of") }} {{ books.length }}
      </div>

      <div class="book-slider__nav">
        <div class="book-slider__nav-left" @click="slideImage(-1)">
          <sprite
            id="app/arrow"
            class="book-slider__nav-left-icon"
            :class="{
              'book-slider__nav-left-icon--active': leftNavIconActive
            }"
          />
        </div>

        <div class="book-slider__nav-cover-wrapper">
          <div class="book-slider__nav-cover-container">
            <transition name="fade" mode="in-out">
              <a :href="samplePageUrl" :key="currentBook.id">
                <book-cover class="book-slider__nav-cover" :sources="currentBook.image" />
              </a>
            </transition>
          </div>
        </div>

        <div class="book-slider__nav-right" @click="slideImage(1)">
          <sprite
            id="app/arrow"
            class="book-slider__nav-right-icon"
            :class="{
              'book-slider__nav-right-icon--active': rightNavIconActive
            }"
          />
        </div>
      </div>

      <div class="book-slider__book-info">
        <div class="book-slider__book-title">
          {{ currentBook.title }}
        </div>

        <div class="book-slider__book-author">
          {{ currentBook.truncatedAuthor }}
        </div>

        <div class="book-slider__book-description" v-html="currentBook.aboutTheBook" />
      </div>

      <div class="book-slider__listen-cta">
        <listen-cta :href="samplePageUrl" />
      </div>

      <div class="book-slider__book-info">
        <div class="book-slider__reading-info">
          <div class="book-slider__info-icon-container">
            <sprite id="app/time" class="book-slider__info-icon" />
          </div>

          {{
            $t("readingInfo", {
              chaptersLength: currentBook.chaptersLength,
              readingDuration: currentBook.readingDuration
            })
          }}
        </div>

        <div class="book-slider__audio-info" v-if="currentBook.hasAudio">
          <div class="book-slider__info-icon-container">
            <sprite id="app/blink" class="book-slider__info-icon" />
          </div>

          {{ $t("audioInfo") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BookCover from "@/shared/book-cover"
import ListenCta from "@/features/listen-cta/components/listen-cta"
import Sprite from "@/shared/sprite"
import "/wds/sprites/app/arrow.svg"
import "/wds/sprites/app/time.svg"
import "/wds/sprites/app/blink.svg"
import events from "@/bl-events"
import getLocale from "@/utils/locale"

export default {
  name: "BookSlider",
  components: {
    BookCover,
    Sprite,
    ListenCta
  },
  props: {
    headline: {
      type: String,
      default: null
    },
    books: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentBookIndex: 0
    }
  },
  computed: {
    currentBook() {
      return this.books[this.currentBookIndex]
    },
    samplePageUrl() {
      return `/${getLocale()}/books/${this.currentBook.slug}/audio-preview?cv=cl`
    },
    leftNavIconActive() {
      return this.currentBookIndex > 0
    },
    rightNavIconActive() {
      return this.currentBookIndex < this.books.length - 1
    },
    currentBookNumber() {
      return this.currentBookIndex + 1
    }
  },
  methods: {
    slideImage(delta) {
      const newIndex = this.currentBookIndex + delta

      if (newIndex < 0) {
        return
      }

      if (newIndex >= this.books.length) {
        return
      }

      const piwikAction = delta > 0 ? "1-right" : "1-left"
      events.track("body-20200104-book_slider", piwikAction, newIndex + 1)

      this.currentBookIndex = newIndex
    }
  },
  i18n: {
    messages: {
      en: {
        book: "Title",
        of: "of",
        readingInfo: "Understand {chaptersLength} key ideas in {readingDuration} min",
        audioInfo: "Audio & text version available"
      },
      de: {
        book: "Titel",
        of: "von",
        readingInfo: "Wissen von {chaptersLength} Kernaussagen in {readingDuration} min",
        audioInfo: "Audio- & Textversion verfügbar"
      },
      es: {
        book: "Título",
        of: "de",
        readingInfo: "Comprende {chaptersLength} ideas clave en {readingDuration} min",
        audioInfo: "Versión de audio y texto disponible"
      }
    }
  }
}
</script>

.
<style lang="scss">
.book-slider {
  background-color: $pale-mint-grey;

  &__container {
    padding: $spacing-48 $spacing-16;
  }

  &__headline {
    text-align: center;
  }

  &__books-counter {
    @include font-style(subheadline, $color: $dark-grey);

    margin-bottom: $spacing-8;

    text-align: center;
  }

  &__nav {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: $spacing-16;
  }

  &__nav-left {
    padding-right: $spacing-8;
  }

  &__nav-left-icon {
    flex-shrink: 0;
    width: auto;

    color: $light-grey;

    transform: rotate(90deg);

    &--active {
      color: $blue;
    }
  }

  &__nav-right {
    padding-left: $spacing-8;
  }

  &__nav-right-icon {
    flex-shrink: 0;
    width: auto;

    color: $light-grey;

    transform: rotate(-90deg);

    &--active {
      color: $blue;
    }
  }

  &__nav-cover-wrapper {
    width: 100%;
  }

  &__nav-cover-container {
    position: relative;

    width: 100%;
    padding-top: 100%;
  }

  &__nav-cover {
    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
  }

  &__book-title {
    @include font-weight(bold);

    margin-bottom: $spacing-4;
  }

  &__book-author {
    @include font-style(caption, $color: $mid-grey);

    margin-bottom: $spacing-8;
  }

  &__book-description {
    @include font-style(caption, $color: $deep-black);

    margin-bottom: $spacing-24;
  }

  &__listen-cta {
    margin-bottom: $spacing-24;
  }

  &__reading-info {
    @include font-style(caption, $color: $midnight);

    display: flex;
    align-items: center;
  }

  &__audio-info {
    @include font-style(caption, $color: $midnight);

    display: flex;
    align-items: center;
    margin-top: $spacing-8;
  }

  &__info-icon-container {
    min-width: $spacing-24;
    margin-right: $spacing-8;
  }

  &__info-icon {
    width: auto;
    height: $spacing-16;
  }

  .fade-enter-active,
  .fade-leave-active {
    @include ease(opacity, 0.25s);
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
